import type {Dayjs} from "dayjs";

const currentTimestamp = ref(Date.now());
let offsetInitialized = false;
let updateTimeInterval: null | ReturnType<typeof setTimeout> = null;
// One computed per timezone, shared by every caller. The clock ticks every second and every mounted
// session card wants to know which calendar day it is, so without sharing that is one dayjs timezone
// conversion per card per second: with the whole agenda mounted (311 cards at WWC26 NA) it measured
// as a ~100ms task every second on a desktop. Shared, it is one conversion per second for the page,
// and because the value (epoch ms of the day's start) only changes at midnight, computeds built on it
// re-run once a day - Vue stops propagation when a computed's value is unchanged.
const dayStartByZone = new Map<string, ComputedRef<number>>();
// One converted instance per (instant, zone), shared by every caller. The timezone plugin formats
// through Intl on every call, and rendering the agenda converted the same few times thousands of
// times: Berlin 2026's 492 sessions have 80 distinct start times, yet dayjs tz() took ~430 ms of a
// ~1.5 s agenda render at 4x CPU. dayjs instances are immutable, so sharing one is safe. Bounded
// because the clock adds a new instant every second; a clear costs one re-conversion per instant.
const zonedCache = new Map<string, Dayjs>();
const ZONED_CACHE_MAX = 2000;
// zoneAbbr's result per "zone|UTC offset", e.g. "Europe/Berlin|120" -> "CEST".
const zoneAbbrByOffset = new Map<string, string>();
export const useCurrentTime = () => {

  const dayjs = useDayjs();
  const settingsStore = useSettingsStore();
  const {currentTimestampOffset} = storeToRefs(settingsStore);

  //region Current time

  if(!offsetInitialized) {
    offsetInitialized = true;
    currentTimestamp.value = Date.now() + currentTimestampOffset.value;
  }

  const currentTimestampFormatted = computed (() => {
    return dayjs(currentTimestamp.value).format('DD/MM/YYYY HH:mm:ss');
  });

  const setCurrentTimestampOffset = (milliseconds: number) => {
    settingsStore.setCurrentTimestampOffset(milliseconds);
  }

  const updateCurrentTime = () => {
    currentTimestamp.value = Date.now() + currentTimestampOffset.value;
  };

  const initAutoUpdate = () => {
    if (updateTimeInterval) {
      clearInterval(updateTimeInterval);
    }
    updateTimeInterval = setInterval(updateCurrentTime, 1000);
  }

  const stopAutoUpdate = () => {
    if (updateTimeInterval) {
      clearInterval(updateTimeInterval);
    }
  }

  //endregion

  //region Date/Time utilities

  const dayJsTz = (date?: string | Date | number, tz?: string) => {
    // No input means "now", which must never come from the cache.
    if(date === undefined || date === null) {
      return dayjs(date).tz(tz);
    }
    // The type is part of the key: dayjs reads the number 1783584900000 as an instant and the
    // string '1783584900000' as something else. A Date is the same instant as its timestamp.
    const key = typeof date === 'string' ? `s${date}|${tz ?? ''}` : `n${date instanceof Date ? date.getTime() : date}|${tz ?? ''}`;
    let zoned = zonedCache.get(key);
    if(!zoned) {
      if(zonedCache.size >= ZONED_CACHE_MAX) {
        zonedCache.clear();
      }
      zoned = dayjs(date).tz(tz);
      zonedCache.set(key, zoned);
    }
    return zoned;
  }

  const dayJsTzIsYesterday = (date: Dayjs, tz?: string) => {
    return date.isSame(dayJsTz(currentTimestamp.value, tz).subtract(1, 'day'), 'day');
  }
  const dayJsTzIsToday = (date: Dayjs, tz?: string) => {
    return date.isSame(dayJsTz(currentTimestamp.value, tz), 'day');
  }

  const dayJsTzIsTomorrow = (date: Dayjs, tz?: string) => {
    return date.isSame(dayJsTz(currentTimestamp.value, tz).add(1, 'day'), 'day');
  }

  // Epoch ms of the start of the current day in `tz`, as a page-wide shared computed (see
  // dayStartByZone). Prefer this over dayJsTzIsToday & co. in anything that is instantiated per
  // list item: those helpers convert the clock on every call, this converts it once per second for
  // everyone and only notifies dependants when the day changes.
  const currentDayStart = (tz: string): ComputedRef<number> => {
    let dayStart = dayStartByZone.get(tz);
    if(!dayStart) {
      dayStart = computed(() => dayJsTz(currentTimestamp.value, tz).startOf('day').valueOf());
      dayStartByZone.set(tz, dayStart);
    }
    return dayStart;
  }

  // dayjs's `z` token renders most European zones as "GMT+2" instead of the
  // familiar "CEST", so those get an explicit abbreviation pair. Zones without
  // an override (e.g. America/Los_Angeles → PDT/PST) use `z` directly. Without
  // a tz the label stays "CET" — parity with the old hardcoded string for
  // events the API serves without a timezone.
  const zoneAbbrOverrides: Record<string, { standard: string, daylight: string }> = {
    'Europe/Berlin': {standard: 'CET', daylight: 'CEST'},
    'Europe/Vienna': {standard: 'CET', daylight: 'CEST'},
    // dayjs `z` renders Kolkata as "GMT+5:30"; no DST there, so both map to IST.
    'Asia/Kolkata': {standard: 'IST', daylight: 'IST'},
  };

  // Every agenda build labels each session's day (492 calls for Berlin 2026), so the label is cached per
  // zone and UTC offset, which is all it depends on: the instant comes from the dayJsTz cache, and the
  // abbreviation is worked out once per (zone, offset). It used to be a fresh timezone conversion plus a
  // second one inside month()/date() per call, ~200 ms of the agenda render at 4x CPU. That month(0)
  // also kept the instant's own offset, so January compared equal to July and summer read "CET".
  const zoneAbbr = (date: Dayjs, tz?: string | null): string => {
    if (!tz) return 'CET';
    const zoned = dayJsTz(date.valueOf(), tz);
    const key = `${tz}|${zoned.utcOffset()}`;
    let abbr = zoneAbbrByOffset.get(key);
    if (abbr === undefined) {
      const override = zoneAbbrOverrides[tz];
      if (override) {
        // Daylight time is an offset above the zone's standard one, the smaller of January's and July's.
        const year = zoned.year();
        const standardOffset = Math.min(dayJsTz(Date.UTC(year, 0, 1, 12), tz).utcOffset(), dayJsTz(Date.UTC(year, 6, 1, 12), tz).utcOffset());
        abbr = zoned.utcOffset() > standardOffset ? override.daylight : override.standard;
      } else {
        abbr = zoned.format('z');
      }
      zoneAbbrByOffset.set(key, abbr);
    }
    return abbr;
  }

  //endregion

  return {
    currentTimestamp,
    currentTimestampFormatted,
    setCurrentTimestampOffset,
    initAutoUpdate,
    stopAutoUpdate,
    dayJsTz,
    dayJsTzIsYesterday,
    dayJsTzIsToday,
    dayJsTzIsTomorrow,
    currentDayStart,
    zoneAbbr
  };
};
